import React, { useState, useEffect, useCallback } from 'react';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { Search, CheckCircle2, XCircle, AlertTriangle, MinusCircle } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { PageHeader, DataTable, StatusDot, EmptyState } from '../design';

const PLAN_TONE = {
  free: 'bg-slate-500/15 text-slate-300',
  starter: 'bg-sky-500/15 text-sky-300',
  growth: 'bg-violet-500/15 text-violet-300',
  scale: 'bg-amber-500/15 text-amber-300',
  enterprise: 'bg-emerald-500/15 text-emerald-300',
};

function UsageBar({ usage }) {
  if (!usage || usage.limit == null) {
    return <span className="gov-text-muted text-xs">{usage ? `${usage.used} used` : '—'}</span>;
  }
  const pct = usage.pct == null ? 0 : Math.min(usage.pct, 100);
  const color = usage.over_limit ? 'bg-rose-500' : usage.near_limit ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded-full bg-[var(--gov-border)] overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs tabular-nums ${usage.over_limit ? 'text-rose-400' : usage.near_limit ? 'text-amber-400' : 'gov-text-secondary'}`}>
        {usage.used}/{usage.limit}
      </span>
    </div>
  );
}

export default function CapabilityInspector() {
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await governanceAPI.getTenants();
        const list = res.data?.tenants || res.data || [];
        setTenants(Array.isArray(list) ? list : []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const inspect = useCallback(async (tid) => {
    if (!tid) return;
    setLoading(true);
    setData(null);
    try {
      const res = await governanceAPI.getCapability(tid);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load capabilities for this tenant');
    } finally {
      setLoading(false);
    }
  }, []);

  const onSelect = (tid) => { setSelected(tid); inspect(tid); };

  const cols = [
    { accessorKey: 'label', header: 'Capability', cell: ({ row }) => (
      <div>
        <div className="font-medium gov-text-primary">{row.original.label}</div>
        <div className="text-[11px] gov-text-muted">{row.original.category}</div>
      </div>
    ) },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => {
      const s = row.original.status;
      if (s === 'enabled') return <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Enabled</span>;
      if (s === 'partial') return <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-medium"><AlertTriangle className="h-3.5 w-3.5" /> {row.original.plan_value}</span>;
      return <span className="inline-flex items-center gap-1.5 text-rose-400 text-xs font-medium"><XCircle className="h-3.5 w-3.5" /> Disabled</span>;
    } },
    { accessorKey: 'plan_value', header: 'Plan Value', cell: ({ row }) => <span className="gov-text-secondary text-xs">{row.original.plan_value}</span> },
    { id: 'usage', header: 'Usage', cell: ({ row }) => row.original.usage ? <UsageBar usage={row.original.usage} /> : <span className="gov-text-muted text-xs">—</span> },
    { id: 'reason', header: 'Why', cell: ({ row }) => row.original.reason ? <span className="text-xs text-rose-300/80">{row.original.reason}</span> : <span className="gov-text-muted text-xs">—</span> },
  ];

  return (
    <div className="space-y-8" data-testid="capability-inspector">
      <PageHeader
        title="Capability Inspector"
        subtitle="What's running for a tenant — and why. Answers support tickets in one screen."
        actions={
          <Select value={selected} onValueChange={onSelect}>
            <SelectTrigger className="w-[280px] h-9" data-testid="capability-tenant-select">
              <Search className="h-4 w-4 mr-2 gov-text-muted" />
              <SelectValue placeholder="Select a tenant…" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              {tenants.map((t) => (
                <SelectItem key={t.id || t.tenant_id} value={t.id || t.tenant_id}>
                  {t.company_name || t.name || t.tenant_id || t.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {!selected && !loading && (
        <EmptyState
          icon={MinusCircle}
          title="Select a tenant to inspect"
          hint="See exactly which features are enabled, near-limit, or blocked — and the reason."
        />
      )}

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border border-[var(--gov-border)] bg-[var(--gov-surface)]" data-testid="capability-summary">
            <div>
              <div className="text-lg font-semibold gov-text-primary">{data.tenant_name}</div>
              <div className="text-xs gov-text-muted font-mono">{data.tenant_id}</div>
            </div>
            <span className={`px-2.5 py-1 rounded text-xs font-semibold capitalize ${PLAN_TONE[data.plan_id] || PLAN_TONE.free}`}>{data.plan_id} plan</span>
            <StatusDot status={data.billing_status === 'active' ? 'ok' : 'warning'} variant="pill" />
            <span className="text-xs gov-text-secondary">AI Mode: <span className={data.live_mode_allowed ? 'text-emerald-400' : 'text-amber-400'}>{data.live_mode_allowed ? 'Live' : 'Simulated'}</span></span>
            <span className="text-xs gov-text-secondary">Credit Balance: <span className="gov-text-primary">${(data.credit_balance_usd || 0).toFixed(2)}</span></span>
          </div>

          <DataTable
            columns={cols}
            data={data.capabilities || []}
            loading={loading}
            testId="capability-table"
            pageSize={30}
            emptyTitle="No capability data"
          />
        </>
      )}
    </div>
  );
}
